import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, FileText, Trash2, ChevronRight, ChevronDown, Layers, List, AlertTriangle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { formatCHF } from '../utils/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 30;

function ComprehensiveTransactions({ customerId }) {
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelledDialog, setShowCancelledDialog] = useState(false);
  const [paymentDetailOpen, setPaymentDetailOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // History features
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Zeitraum-Filter: 'all' | 'upcoming' | 'past' (07/2026 — vergangene Termine
  // waren praktisch unerreichbar, da erst alle kommenden gelistet wurden).
  const [periodFilter, setPeriodFilter] = useState('all');
  const [groupSeries, setGroupSeries] = useState(true);
  const [expandedSeries, setExpandedSeries] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mode: 'single', appt: null, seriesId: null, count: 0 });
  
  // Filter für Export
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth.toString());

  useEffect(() => {
    if (!customerId) return;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/customers/${customerId}/comprehensive-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTransactionData(res.data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Fehler beim Laden der Transaktionen');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [customerId]);


  const {
    allAppointments,
    cancelledAppointments,
    summary,
    payments
  } = useMemo(() => {
    return {
      allAppointments: transactionData?.appointments || [],
      cancelledAppointments: transactionData?.cancelled_appointments || [],
      summary: transactionData?.summary || {},
      payments: transactionData?.grouped_transactions?.flatMap(g => g.payments || []) || []
    };
  }, [transactionData]);

  const activeAppointments = useMemo(
    () => allAppointments.filter(a => a.status !== 'cancelled'),
    [allAppointments]
  );

  const paymentsByAppointment = useMemo(() => {
    const map = {};
    payments.forEach(p => {
      if (p.appointment_id) {
        map[p.appointment_id] = p;
      }
    });
    return map;
  }, [payments]);

  const appointmentsById = useMemo(() => {
    const map = {};
    allAppointments.forEach((apt) => {
      if (apt?.id) {
        map[apt.id] = apt;
      }
    });
    return map;
  }, [allAppointments]);

  // Iter 297g: Split in Kommende und Vergangene Termine.
  // Kommende: aufsteigend (nächstes zuerst).
  // Vergangene: absteigend (neueste zuerst).
  // Vorher: alle Termine zusammen absteigend → Termine von 2051 (Müll-Daten)
  // erschienen vor den heutigen.
  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const now = new Date();
    const upcoming = [];
    const past = [];
    activeAppointments.forEach(apt => {
      if (!apt.start_time) return;
      const d = new Date(apt.start_time);
      if (d >= now) upcoming.push(apt);
      else past.push(apt);
    });
    upcoming.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    past.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    return { upcomingAppointments: upcoming, pastAppointments: past };
  }, [activeAppointments]);

  const appointmentsByMonth = useMemo(() => {
    const grouped = {};
    const now = new Date();
    activeAppointments.forEach(apt => {
      if (apt.start_time) {
        const date = new Date(apt.start_time);
        // 08/2026 Fix: Innerhalb des aktuellen Monats nach Termin (nicht nach
        // Monat) in Kommend/Vergangen trennen — vorher landeten bereits
        // abgerechnete Termine vom Monatsanfang unter "Kommende Termine".
        const bucket = date >= now ? 'up' : 'past';
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}|${bucket}`;
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(apt);
      }
    });
    return grouped;
  }, [activeAppointments]);

  const sortedMonths = useMemo(() => {
    // Kommende Monate aufsteigend (nächster zuerst), dann Vergangene
    // absteigend (neueste zuerst). Zeitraum-Filter berücksichtigen.
    const all = Object.keys(appointmentsByMonth);
    const upcoming = all.filter(k => k.endsWith('|up')).sort();
    const past = all.filter(k => k.endsWith('|past')).sort().reverse();
    if (periodFilter === 'upcoming') return upcoming;
    if (periodFilter === 'past') return past;
    return [...upcoming, ...past];
  }, [appointmentsByMonth, periodFilter]);

  // Termine im aktuell gewählten Zeitraum (für Zähler & "Mehr anzeigen").
  const periodAppointments = useMemo(() => {
    if (periodFilter === 'upcoming') return upcomingAppointments;
    if (periodFilter === 'past') return pastAppointments;
    return activeAppointments;
  }, [periodFilter, upcomingAppointments, pastAppointments, activeAppointments]);

  // Paginate months: compute total visible appointments and cut off
  const paginatedMonths = useMemo(() => {
    const out = [];
    let count = 0;
    for (const monthKey of sortedMonths) {
      const apts = appointmentsByMonth[monthKey] || [];
      const remaining = visibleCount - count;
      if (remaining <= 0) break;
      if (apts.length <= remaining) {
        out.push({ monthKey, appointments: apts });
        count += apts.length;
      } else {
        out.push({ monthKey, appointments: apts.slice(0, remaining) });
        count = visibleCount;
        break;
      }
    }
    return out;
  }, [sortedMonths, appointmentsByMonth, visibleCount]);

  const hasMoreAppointments = periodAppointments.length > visibleCount;

  // Navigate to AppointmentsPage with selected appointment
  const handleOpenAppointment = useCallback((apt) => {
    if (!apt?.start_time) {
      navigate('/appointments');
      return;
    }
    const d = new Date(apt.start_time);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    navigate(`/appointments?date=${dateStr}&selected=${apt.id}`);
  }, [navigate]);

  // Delete actions — optimistic
  const performDelete = useCallback(async (apt, scope) => {
    const originalList = transactionData;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/appointments/${apt.id}?delete_scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Optimistically update state locally
      setTransactionData((prev) => {
        if (!prev) return prev;
        const filterFn = scope === 'future' && apt.series_id
          ? (a) => a.series_id !== apt.series_id
          : (a) => a.id !== apt.id;
        return {
          ...prev,
          appointments: (prev.appointments || []).filter(filterFn),
        };
      });
      // Gutschrift-Info aus der Antwort anzeigen (bezahlte Termine werden
      // beim Löschen automatisch dem Kundenkonto gutgeschrieben)
      const creditedAmount = res.data?.credit_amount || 0;
      toast.success(
        scope === 'future' && apt.series_id
          ? 'Serie + zukünftige Termine storniert'
          : creditedAmount > 0
            ? `Termin gelöscht · CHF ${creditedAmount.toFixed(2)} als Guthaben gutgeschrieben`
            : 'Termin gelöscht'
      );
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
      setTransactionData(originalList); // rollback
    } finally {
      setDeleteDialog({ open: false, mode: 'single', appt: null, seriesId: null, count: 0 });
    }
  }, [transactionData]);

  const toggleSeries = (seriesId) => {
    setExpandedSeries((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId);
      else next.add(seriesId);
      return next;
    });
  };


  // Month names in German
  const monthNames = {
    '01': 'Januar', '02': 'Februar', '03': 'März', '04': 'April',
    '05': 'Mai', '06': 'Juni', '07': 'Juli', '08': 'August',
    '09': 'September', '10': 'Oktober', '11': 'November', '12': 'Dezember'
  };

  const getStatusConfig = (status, billingStatus) => {
    if (status === 'cancelled') {
      return { label: 'Storniert', className: 'bg-red-100 text-red-800', icon: '❌' };
    }
    if (billingStatus === 'paid') {
      return { label: 'Bezahlt', className: 'bg-green-100 text-green-800', icon: '✅' };
    }
    if (billingStatus === 'billed') {
      return { label: 'Abgerechnet', className: 'bg-blue-100 text-blue-800', icon: '📄' };
    }
    if (status === 'completed') {
      return { label: 'Abgeschlossen', className: 'bg-green-100 text-green-800', icon: '✅' };
    }
    if (status === 'scheduled') {
      return { label: 'Geplant', className: 'bg-yellow-100 text-yellow-800', icon: '📅' };
    }
    return { label: status, className: 'bg-gray-100 text-gray-800', icon: '•' };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Kein Datum';
    const date = new Date(dateString);
    return date.toLocaleString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  const createSimplePdf = useCallback((title, summaryLines, headers, rows, fileName) => {
    const doc = new jsPDF();
    let y = 15;
    doc.setFontSize(14);
    doc.text(title, 14, y);
    y += 8;

    doc.setFontSize(10);
    (summaryLines || []).forEach((line) => {
      doc.text(line, 14, y);
      y += 6;
    });

    if (headers?.length) {
      doc.setFontSize(9);
      doc.text(headers.join(' | '), 14, y);
      y += 6;
    }

    rows.forEach((row) => {
      const line = row.join(' | ');
      if (y > 280) {
        doc.addPage();
        y = 15;
      }
      doc.text(line, 14, y);
      y += 6;
    });

    doc.save(fileName);
  }, []);

  // Export-Funktionen
  const exportToPDF = async (period = 'all') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/customers/${customerId}/history-export?format=pdf&period=${period}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const periodLabel = period === 'month' ? 'Monat' : period === 'year' ? 'Jahr' : 'Gesamt';
      link.download = `Trainingshistorie_${periodLabel}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    }
  };

  const exportToCSV = (period = 'all') => {
    let appointmentsToExport = [...activeAppointments];
    
    // Filter by period
    if (period === 'month') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      appointmentsToExport = appointmentsToExport.filter(a => {
        const d = new Date(a.start_time);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      });
    } else if (period === 'year') {
      const currentYear = new Date().getFullYear();
      appointmentsToExport = appointmentsToExport.filter(a => {
        return new Date(a.start_time).getFullYear() === currentYear;
      });
    }
    
    // Create CSV
    const headers = ['Datum', 'Zeit', 'Service', 'Trainer', 'Status', 'Bezahlt mit'];
    const rows = appointmentsToExport.map(a => [
      new Date(a.start_time).toLocaleDateString('de-CH'),
      formatTime(a.start_time),
      a.service_name || '',
      a.trainer_name || '',
      a.status || '',
      a.payment_method || a.billing_status || ''
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const periodLabel = period === 'month' ? 'Monat' : period === 'year' ? 'Jahr' : 'Gesamt';
    link.download = `Trainingshistorie_${periodLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV erfolgreich heruntergeladen');
  };

  const exportPaymentsCSV = useCallback(() => {
    if (!payments.length) {
      toast.error('Keine Zahlungen verfügbar');
      return;
    }
    const headers = ['Datum', 'Betrag', 'Status', 'Zahlungsart', 'Service', 'Termin'];
    const rows = payments.map((payment) => {
      const appointment = appointmentsById[payment.appointment_id];
      return [
        payment.created_at ? new Date(payment.created_at).toLocaleString('de-CH') : '-',
        `CHF ${formatCHF(payment.amount)}`,
        payment.status || '',
        payment.payment_method || '',
        appointment?.service_name || '',
        appointment?.start_time ? new Date(appointment.start_time).toLocaleString('de-CH') : ''
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Zahlungen_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('Zahlungen als CSV exportiert');
  }, [appointmentsById, payments]);

  const exportPaymentsPDF = useCallback(() => {
    if (!payments.length) {
      toast.error('Keine Zahlungen verfügbar');
      return;
    }
    const headers = ['Datum', 'Betrag', 'Status', 'Zahlungsart', 'Service', 'Termin'];
    const rows = payments.map((payment) => {
      const appointment = appointmentsById[payment.appointment_id];
      return [
        payment.created_at ? new Date(payment.created_at).toLocaleString('de-CH') : '-',
        `CHF ${formatCHF(payment.amount)}`,
        payment.status || '',
        payment.payment_method || '',
        appointment?.service_name || '',
        appointment?.start_time ? new Date(appointment.start_time).toLocaleString('de-CH') : ''
      ];
    });
    createSimplePdf(
      'Zahlungen',
      [`Anzahl: ${payments.length}`],
      headers,
      rows,
      `Zahlungen_${new Date().toISOString().split('T')[0]}.pdf`
    );
  }, [appointmentsById, createSimplePdf, payments]);

  const fetchInvoices = useCallback(async () => {
    if (!customerId) return [];
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/invoices`, {
      params: { customer_id: customerId },
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data || [];
  }, [customerId]);

  const exportInvoicesCSV = useCallback(async () => {
    try {
      const invoices = await fetchInvoices();
      if (!invoices.length) {
        toast.error('Keine Rechnungen verfügbar');
        return;
      }
      const headers = ['Rechnungsnummer', 'Datum', 'Status', 'Betrag', 'Fällig am'];
      const rows = invoices.map((inv) => {
        const amount = inv.total ?? inv.total_amount ?? inv.amount ?? 0;
        const date = inv.created_at || inv.invoice_date || inv.date;
        return [
          inv.invoice_number || inv.id || '',
          date ? new Date(date).toLocaleDateString('de-CH') : '',
          inv.status || '',
          `CHF ${formatCHF(amount)}`,
          inv.due_date ? new Date(inv.due_date).toLocaleDateString('de-CH') : ''
        ];
      });
      const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnungen_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Rechnungen als CSV exportiert');
    } catch (error) {
      toast.error('Fehler beim Export der Rechnungen');
    }
  }, [fetchInvoices]);

  const exportInvoicesPDF = useCallback(async () => {
    try {
      const invoices = await fetchInvoices();
      if (!invoices.length) {
        toast.error('Keine Rechnungen verfügbar');
        return;
      }
      const headers = ['Rechnungsnummer', 'Datum', 'Status', 'Betrag', 'Fällig am'];
      const rows = invoices.map((inv) => {
        const amount = inv.total ?? inv.total_amount ?? inv.amount ?? 0;
        const date = inv.created_at || inv.invoice_date || inv.date;
        return [
          inv.invoice_number || inv.id || '',
          date ? new Date(date).toLocaleDateString('de-CH') : '',
          inv.status || '',
          `CHF ${formatCHF(amount)}`,
          inv.due_date ? new Date(inv.due_date).toLocaleDateString('de-CH') : ''
        ];
      });
      createSimplePdf(
        'Rechnungen',
        [`Anzahl: ${invoices.length}`],
        headers,
        rows,
        `Rechnungen_${new Date().toISOString().split('T')[0]}.pdf`
      );
    } catch (error) {
      toast.error('Fehler beim PDF-Export der Rechnungen');
    }
  }, [fetchInvoices, createSimplePdf]);

  const handleOpenPaymentDetails = (payment, appointment) => {
    setSelectedPayment(payment);
    setSelectedAppointment(appointment);
    setPaymentDetailOpen(true);
  };

  const handleExportPayment = () => {
    if (!selectedPayment) return;
    const headers = ['Zahlung ID', 'Datum', 'Betrag', 'Status', 'Zahlungsart', 'Service', 'Termin'];
    const rows = [[
      selectedPayment.id || '',
      selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleString('de-CH') : '-',
      selectedPayment.amount || 0,
      selectedPayment.status || '',
      selectedPayment.payment_method || '',
      selectedAppointment?.service_name || '',
      selectedAppointment?.start_time ? new Date(selectedAppointment.start_time).toLocaleString('de-CH') : ''
    ]];

    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Zahlung_${selectedPayment.id || 'detail'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('Zahlung als CSV exportiert');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500" data-testid="customer-transactions-loading">
          Lädt Transaktionen...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-transactions-container">
      {/* Header with Summary, Export Buttons and Cancelled Link.
          Responsiv (06/2026): Bei schmalem Viewport/Zoom stapeln sich
          Zusammenfassung & Export untereinander statt horizontal
          überzulaufen (Bug: Modal musste horizontal gescrollt werden). */}
      <div className="flex flex-col xl:flex-row items-stretch xl:items-start justify-between gap-4">
        <Card className="flex-1 min-w-0 border-2 border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-blue-900">📊 Zusammenfassung</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-2 bg-white rounded border" data-testid="summary-past">
                <div className="text-2xl font-bold text-gray-700">{pastAppointments.length}</div>
                <div className="text-xs text-gray-600">Vergangene</div>
              </div>
              <div className="text-center p-2 bg-white rounded border" data-testid="summary-upcoming">
                <div className="text-2xl font-bold text-blue-600">{upcomingAppointments.length}</div>
                <div className="text-xs text-gray-600">Kommende</div>
              </div>
              <div
                className={`text-center p-2 rounded border ${(summary.open_invoices_count || 0) > 0 ? 'bg-red-50 border-red-300' : 'bg-white'}`}
                data-testid="summary-open-invoices"
              >
                <div className={`text-2xl font-bold ${(summary.open_invoices_count || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {summary.open_invoices_count || 0}
                </div>
                <div className="text-xs text-gray-600">Offene Rechn.</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Export Buttons */}
        <div className="flex flex-col gap-3" data-testid="customer-transactions-export-panel">
          <div className="text-xs font-semibold text-gray-600">📥 Export</div>
          <div className="space-y-2">
            <div className="text-[11px] font-medium text-gray-500" data-testid="customer-transactions-export-appointments-label">
              Termine
            </div>
            <div className="flex flex-wrap gap-1" data-testid="customer-transactions-export-appointments-csv">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-month"
                onClick={() => exportToCSV('month')}
              >
                <Download size={12} className="mr-1" />
                CSV Monat
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-year"
                onClick={() => exportToCSV('year')}
              >
                <Download size={12} className="mr-1" />
                CSV Jahr
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-all"
                onClick={() => exportToCSV('all')}
              >
                <Download size={12} className="mr-1" />
                CSV Alle
              </Button>
            </div>
            <div className="flex flex-wrap gap-1" data-testid="customer-transactions-export-appointments-pdf">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-pdf-month"
                onClick={() => exportToPDF('month')}
              >
                <FileText size={12} className="mr-1" />
                PDF Monat
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-pdf-year"
                onClick={() => exportToPDF('year')}
              >
                <FileText size={12} className="mr-1" />
                PDF Jahr
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-pdf-all"
                onClick={() => exportToPDF('all')}
              >
                <FileText size={12} className="mr-1" />
                PDF Alle
              </Button>
            </div>
            <div className="text-[11px] font-medium text-gray-500" data-testid="customer-transactions-export-payments-label">
              Zahlungen
            </div>
            <div className="flex flex-wrap gap-1" data-testid="customer-transactions-export-payments">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-payments-csv"
                onClick={exportPaymentsCSV}
              >
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-payments-pdf"
                onClick={exportPaymentsPDF}
              >
                PDF
              </Button>
            </div>
            <div className="text-[11px] font-medium text-gray-500" data-testid="customer-transactions-export-invoices-label">
              Rechnungen
            </div>
            <div className="flex flex-wrap gap-1" data-testid="customer-transactions-export-invoices">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-invoices-csv"
                onClick={exportInvoicesCSV}
              >
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="customer-transactions-export-invoices-pdf"
                onClick={exportInvoicesPDF}
              >
                PDF
              </Button>
            </div>
          </div>
        </div>
        
        {/* Abgesagte Buchungen Link - wie bei Mindbody */}
        {cancelledAppointments.length > 0 && (
          <div className="ml-2">
            <Button
              variant="link"
              className="text-red-600 hover:text-red-800 underline font-medium"
              data-testid="customer-transactions-cancelled-open"
              onClick={() => setShowCancelledDialog(true)}
            >
              Abgesagte Buchungen
            </Button>
          </div>
        )}
      </div>

      {/* Offene Rechnungen — vorher in den Kundendetails nicht ersichtlich
          (z.B. Punkte-Paket-Rechnungen ohne Termin-Bezug, 06/2026) */}
      {(transactionData?.open_invoices || []).length > 0 && (
        <div className="border-2 border-red-200 bg-red-50 rounded-lg p-3" data-testid="open-invoices-banner">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-red-800 text-sm">
              ⚠️ Offene Rechnungen ({transactionData.open_invoices.length})
            </span>
            <span className="font-bold text-red-700">
              CHF {(transactionData.summary?.open_invoices_total || 0).toFixed(2)}
            </span>
          </div>
          <div className="space-y-1">
            {transactionData.open_invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5 border border-red-100"
                data-testid={`open-invoice-row-${inv.invoice_number}`}
              >
                <span className="font-mono text-xs text-gray-700">{inv.invoice_number}</span>
                <span className="text-xs text-gray-500">
                  {inv.due_date ? `fällig ${new Date(inv.due_date).toLocaleDateString('de-CH')}` : ''}
                  {inv.due_date && new Date(inv.due_date) < new Date() && (
                    <span className="text-red-600 font-semibold ml-1">überfällig</span>
                  )}
                </span>
                <span className="font-semibold text-gray-900">CHF {(inv.amount || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancelled Appointments Dialog - wie bei Mindbody */}
      <Dialog open={showCancelledDialog} onOpenChange={setShowCancelledDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="customer-transactions-cancelled-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-800 flex items-center gap-2" data-testid="customer-transactions-cancelled-title">
              ❌ Abgesagte Buchungen ({cancelledAppointments.length})
            </DialogTitle>
          </DialogHeader>
          
          {/* Summary */}
          <div className="flex gap-4 mb-4 text-sm">
            <span className="text-orange-700">
              👤 Vom Kunden storniert: <strong>{summary.cancelled_by_customer || 0}</strong>
            </span>
            <span className="text-red-700">
              👨‍💼 Vom Trainer storniert: <strong>{summary.cancelled_by_trainer || 0}</strong>
            </span>
          </div>
          
          {/* Table Header */}
          <div className="bg-gray-100 p-2 rounded-t border text-xs font-semibold grid grid-cols-12 gap-2">
            <div className="col-span-3">Datum</div>
            <div className="col-span-3">Beschreibung</div>
            <div className="col-span-2">Termine</div>
            <div className="col-span-2">Storniert von</div>
            <div className="col-span-2">Storniert am</div>
          </div>
          
          {/* Table Rows */}
          <div className="border border-t-0 rounded-b divide-y">
            {cancelledAppointments.map((apt, idx) => (
              <div key={idx} className={`p-2 text-sm grid grid-cols-12 gap-2 hover:bg-red-50 ${apt.is_series ? 'bg-purple-50' : ''}`}>
                <div className="col-span-3 text-gray-700">
                  {formatDateTime(apt.start_time)}
                </div>
                <div className="col-span-3">
                  {apt.is_series && (
                    <span className="bg-purple-200 text-purple-800 text-xs px-1 rounded mr-1">Serie</span>
                  )}
                  <span className="font-medium">{apt.service_name || 'Training'}</span>
                  {apt.customer_notified === false && (
                    <span
                      className="ml-1 inline-block bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-semibold px-1 rounded"
                      title="Kunde wurde über diese Stornierung NICHT benachrichtigt"
                      data-testid={`history-not-informed-badge-${idx}`}
                    >
                      🔕 nicht informiert
                    </span>
                  )}
                  {apt.cancellation_reason && !/^Storniert durch /.test(apt.cancellation_reason) && (
                    <div className="text-xs text-gray-500 italic mt-0.5" data-testid={`history-cancellation-reason-${idx}`}>
                      Grund: {apt.cancellation_reason}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  {apt.is_series ? (
                    <span className="font-bold text-purple-700">{apt.appointment_count} Termine</span>
                  ) : (
                    <span className="text-gray-500">1 Termin</span>
                  )}
                </div>
                <div className="col-span-2">
                  <Badge className={apt.cancelled_by === 'customer' ? 'bg-orange-100 text-orange-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>
                    {apt.cancelled_by === 'customer' ? '👤 Kunde' : '👨‍💼 Trainer'}
                  </Badge>
                  {apt.cancelled_by_name && (
                    <div className="text-xs text-gray-700 mt-1 font-medium" data-testid="cancelled-by-name">
                      {apt.cancelled_by_name}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-gray-500 text-xs">
                  {apt.cancelled_at ? formatDate(apt.cancelled_at) : '-'}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentDetailOpen} onOpenChange={setPaymentDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="customer-payment-detail-dialog">
          <DialogHeader>
            <DialogTitle data-testid="customer-payment-detail-title">Zahlungsdetails</DialogTitle>
          </DialogHeader>
          {selectedPayment ? (
            <div className="space-y-4" data-testid="customer-payment-detail-body">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Betrag</p>
                  <p className="text-lg font-semibold" data-testid="customer-payment-detail-amount">
                    CHF {formatCHF(selectedPayment.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className={`text-sm font-medium ${selectedPayment.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`} data-testid="customer-payment-detail-status">
                    {selectedPayment.status === 'completed' ? 'Bezahlt' : 'Offen'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Zahlungsart</p>
                  <p className="text-sm" data-testid="customer-payment-detail-method">{selectedPayment.payment_method || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Datum</p>
                  <p className="text-sm" data-testid="customer-payment-detail-date">
                    {selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleString('de-CH') : '-'}
                  </p>
                </div>
              </div>

              {selectedAppointment && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm" data-testid="customer-payment-detail-appointment">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Service</span>
                    <span>{selectedAppointment.service_name || '-'}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500">Termin</span>
                    <span>{selectedAppointment.start_time ? new Date(selectedAppointment.start_time).toLocaleString('de-CH') : '-'}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2" data-testid="customer-payment-detail-actions">
                <Button size="sm" variant="outline" onClick={handleExportPayment} data-testid="customer-payment-detail-export">
                  CSV exportieren
                </Button>
                {selectedPayment.invoice_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="customer-payment-detail-invoice"
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      try {
                        const res = await axios.get(
                          `${API}/invoices/${selectedPayment.invoice_id}/pdf`,
                          {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob',
                          }
                        );
                        const blob = new Blob([res.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `Rechnung_${selectedPayment.invoice_id}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        toast.error('Fehler beim Download der Rechnung');
                      }
                    }}
                  >
                    Rechnung PDF
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500" data-testid="customer-payment-detail-empty">Keine Details verfügbar.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointments by Month - nur aktive Termine + History-Controls */}
      {sortedMonths.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 justify-between px-1" data-testid="history-controls">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" data-testid="history-period-filter">
              {[
                { key: 'all', label: 'Alle' },
                { key: 'upcoming', label: 'Kommende' },
                { key: 'past', label: 'Vergangene' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`history-period-${opt.key}`}
                  onClick={() => { setPeriodFilter(opt.key); setVisibleCount(PAGE_SIZE); }}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${periodFilter === opt.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500">
              {visibleCount >= periodAppointments.length
                ? `Alle ${periodAppointments.length} Termine`
                : `${Math.min(visibleCount, periodAppointments.length)} von ${periodAppointments.length} Terminen`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7 text-red-700 hover:bg-red-50 border-red-200"
              onClick={async () => {
                // Sicherheits-Confirmation in 2 Stufen — bei grossen Counts
                // (>1000) MUSS der Admin den Count tippen, bei kleineren
                // reicht ein einfaches OK.
                const total = activeAppointments.length;
                let confirmed = false;
                if (total > 50) {
                  const typed = window.prompt(
                    `⚠️ Du bist dabei, ALLE ${total} aktiven Termine dieses Kunden zu löschen.\n\n` +
                    `Diese Aktion kann nicht rückgängig gemacht werden.\n\n` +
                    `Tippe zur Bestätigung die Zahl ${total} ein:`
                  );
                  confirmed = typed && parseInt(typed, 10) === total;
                  if (typed !== null && !confirmed) {
                    alert(`Falsche Zahl eingegeben. Aktion abgebrochen.`);
                    return;
                  }
                } else {
                  confirmed = window.confirm(
                    `Sollen wirklich ALLE ${total} aktiven Termine dieses Kunden gelöscht werden?\n\n` +
                    `Diese Aktion kann nicht rückgängig gemacht werden.`
                  );
                }
                if (!confirmed) return;

                try {
                  const token = localStorage.getItem('token');
                  const res = await axios.delete(
                    `${API}/customers/${customerId}/appointments/all?only_future=false`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  const cancelled = res.data?.cancelled || 0;
                  const credits = res.data?.credits_refunded || 0;
                  toast.success(
                    `${cancelled} Termine storniert${credits > 0 ? ` · ${credits}× Guthaben zurückerstattet` : ''}`
                  );
                  // Daten neu laden
                  setLoading(true);
                  const refresh = await axios.get(
                    `${API}/customers/${customerId}/comprehensive-transactions`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setTransactionData(refresh.data);
                } catch (err) {
                  console.error('Bulk delete failed:', err);
                  toast.error(
                    err.response?.data?.detail || 'Fehler beim Löschen aller Termine'
                  );
                } finally {
                  setLoading(false);
                }
              }}
              data-testid="history-delete-all-appointments-btn"
            >
              <Trash2 className="h-3 w-3" />
              Alle löschen
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7"
              onClick={() => setGroupSeries((v) => !v)}
              data-testid="history-group-toggle"
            >
              {groupSeries ? <Layers className="h-3 w-3" /> : <List className="h-3 w-3" />}
              {groupSeries ? 'Serien gruppiert' : 'Flache Liste'}
            </Button>
          </div>
        </div>
      )}

      {sortedMonths.length === 0 ? (
        <Card data-testid="customer-transactions-empty">
          <CardContent className="p-6 text-center text-gray-500">
            Noch keine Termine vorhanden
          </CardContent>
        </Card>
      ) : (
        paginatedMonths.map(({ monthKey, appointments: monthAppointments }, _mIdx, _arr) => {
          const [year, month] = monthKey.split('|')[0].split('-');
          const monthLabel = `${monthNames[month]} ${year}`;
          const monthlyTotal = monthAppointments.reduce((sum, apt) => sum + (apt.actual_price || 0), 0);

          // Trenner einfügen, wenn dieser Block der erste VERGANGENE ist
          const _isPast = monthKey.endsWith('|past');
          const _prevMonth = _mIdx > 0 ? _arr[_mIdx - 1].monthKey : null;
          const _prevIsPast = _prevMonth ? _prevMonth.endsWith('|past') : false;
          const _showPastDivider = _isPast && !_prevIsPast;

          // 06/2026 — Sort-Richtung: kommende Monate aufsteigend (früheste
          // Termine zuerst, sodass 11:30 vor 12:00 erscheint), vergangene
          // Monate absteigend (jüngste zuerst).
          const _sortAsc = (a, b) => new Date(a.start_time) - new Date(b.start_time);
          const _sortDesc = (a, b) => new Date(b.start_time) - new Date(a.start_time);
          const _sorter = _isPast ? _sortDesc : _sortAsc;

          // Group by series_id when toggle is on — show a single entry per series.
          let renderItems = [...monthAppointments].sort(_sorter);
          if (groupSeries) {
            const seenSeries = new Map(); // series_id → array of appts
            const standalone = [];
            for (const apt of renderItems) {
              const sid = apt.series_id;
              if (sid) {
                if (!seenSeries.has(sid)) seenSeries.set(sid, []);
                seenSeries.get(sid).push(apt);
              } else {
                standalone.push(apt);
              }
            }
            // Build a flat list with series-group wrappers and standalone appts
            renderItems = [];
            const inserted = new Set();
            for (const apt of [...monthAppointments].sort(_sorter)) {
              if (apt.series_id && !inserted.has(apt.series_id)) {
                inserted.add(apt.series_id);
                const group = seenSeries.get(apt.series_id) || [apt];
                renderItems.push({ _kind: 'series', seriesId: apt.series_id, appointments: group, firstApt: group[0] });
              } else if (!apt.series_id) {
                renderItems.push({ _kind: 'single', apt });
              }
            }
          } else {
            renderItems = renderItems.map((apt) => ({ _kind: 'single', apt }));
          }

          return (
            <React.Fragment key={monthKey}>
              {_showPastDivider && (
                <div className="flex items-center gap-3 my-2 text-gray-400" data-testid="history-past-divider">
                  <div className="flex-1 h-px bg-gray-300" />
                  <span className="text-xs font-semibold uppercase tracking-wider">📜 Vergangene Termine</span>
                  <div className="flex-1 h-px bg-gray-300" />
                </div>
              )}
              {!_showPastDivider && _mIdx === 0 && !_isPast && (
                <div className="flex items-center gap-3 my-2 text-yellow-700" data-testid="history-upcoming-divider">
                  <div className="flex-1 h-px bg-yellow-300" />
                  <span className="text-xs font-semibold uppercase tracking-wider">📅 Kommende Termine</span>
                  <div className="flex-1 h-px bg-yellow-300" />
                </div>
              )}
              <Card className="border-2" data-testid={`customer-transactions-month-${monthKey}`}>
              <CardHeader className="bg-gray-50 border-b py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-bold text-gray-800">
                    📅 {monthLabel}
                  </CardTitle>
                  <div className="text-right">
                    <span className="text-sm font-bold text-green-700">CHF {formatCHF(monthlyTotal)}</span>
                    <span className="text-xs text-gray-500 ml-2">({monthAppointments.length} Termine)</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {renderItems.map((row, idx) => {
                    if (row._kind === 'series') {
                      const { seriesId, appointments: seriesAppts, firstApt } = row;
                      const isOpen = expandedSeries.has(seriesId);
                      const statusCfg = getStatusConfig(firstApt.status, firstApt.billing_status);
                      return (
                        <div key={`series-${seriesId}`} className="rounded-lg border-2 border-purple-100 bg-purple-50/30 shadow-sm" data-testid={`history-series-${seriesId}`}>
                          <div className="p-3 flex items-center justify-between gap-2">
                            <button
                              type="button"
                              className="flex items-center gap-2 flex-1 text-left"
                              onClick={() => toggleSeries(seriesId)}
                              data-testid={`history-series-toggle-${seriesId}`}
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4 text-purple-600" /> : <ChevronRight className="h-4 w-4 text-purple-600" />}
                              <span className="text-lg">{statusCfg.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{firstApt.service_name || 'Training'}</p>
                                  <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0">🔗 Serie · {seriesAppts.length}</Badge>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">Klicken zum Aus-/Einklappen</p>
                              </div>
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50 h-8 px-2"
                              onClick={() => setDeleteDialog({ open: true, mode: 'series', appt: firstApt, seriesId, count: seriesAppts.length })}
                              data-testid={`history-delete-series-${seriesId}`}
                              title="Gesamte Serie stornieren"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="text-xs ml-1 hidden sm:inline">Serie</span>
                            </Button>
                          </div>
                          {isOpen && (
                            <div className="px-3 pb-3 pt-0 space-y-2 border-t border-purple-200 bg-white/50">
                              {seriesAppts.map((apt, i) => (
                                <AppointmentRow
                                  key={`${seriesId}-${apt.id || i}`}
                                  apt={apt}
                                  payment={paymentsByAppointment[apt.id]}
                                  statusCfg={getStatusConfig(apt.status, apt.billing_status)}
                                  onOpen={handleOpenAppointment}
                                  onDelete={() => setDeleteDialog({ open: true, mode: 'single', appt: apt, seriesId: null, count: 1 })}
                                  onPaymentDetails={() => handleOpenPaymentDetails(paymentsByAppointment[apt.id], apt)}
                                  formatDateTime={formatDateTime}
                                  formatTime={formatTime}
                                  compact
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    // Single appointment
                    const apt = row.apt;
                    const statusCfg = getStatusConfig(apt.status, apt.billing_status);
                    return (
                      <AppointmentRow
                        key={apt.id || idx}
                        apt={apt}
                        payment={paymentsByAppointment[apt.id]}
                        statusCfg={statusCfg}
                        onOpen={handleOpenAppointment}
                        onDelete={() => setDeleteDialog({ open: true, mode: 'single', appt: apt, seriesId: null, count: 1 })}
                        onPaymentDetails={() => handleOpenPaymentDetails(paymentsByAppointment[apt.id], apt)}
                        formatDateTime={formatDateTime}
                        formatTime={formatTime}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            </React.Fragment>
          );
        })
      )}

      {/* Mehr anzeigen Button */}
      {hasMoreAppointments && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
            className="gap-2"
            data-testid="history-load-more-btn"
          >
            <ChevronDown className="h-4 w-4" />
            Mehr anzeigen ({periodAppointments.length - visibleCount} weitere)
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, mode: 'single', appt: null, seriesId: null, count: 0 })}>
        <DialogContent className="max-w-md" data-testid="history-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {deleteDialog.mode === 'series' ? 'Gesamte Serie stornieren?' : 'Termin stornieren?'}
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.mode === 'series'
                ? `Alle ${deleteDialog.count} Termine dieser Serie (inkl. zukünftige) werden storniert. Dies kann nicht rückgängig gemacht werden.`
                : `"${deleteDialog.appt?.service_name || 'Dieser Termin'}" wird storniert. Dies kann nicht rückgängig gemacht werden.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, mode: 'single', appt: null, seriesId: null, count: 0 })}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!deleteDialog.appt) return;
                if (deleteDialog.mode === 'series') {
                  if (!window.confirm(`Bist du WIRKLICH sicher? ${deleteDialog.count || ''} Termine dieser Serie werden unwiderruflich storniert. Dies kann NICHT rückgängig gemacht werden.`)) return;
                }
                performDelete(deleteDialog.appt, deleteDialog.mode === 'series' ? 'future' : 'this');
              }}
              data-testid="history-delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deleteDialog.mode === 'series' ? 'Ja, Serie stornieren' : 'Ja, stornieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Single appointment row used both standalone and inside series groups
function AppointmentRow({ apt, payment, statusCfg, onOpen, onDelete, onPaymentDetails, formatDateTime, formatTime, compact }) {
  return (
    <div
      className={`${compact ? 'p-2.5' : 'p-3'} bg-white rounded-lg border shadow-sm transition hover:border-blue-300 hover:shadow-md`}
      data-testid={`history-appt-${apt.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          className="flex-1 text-left min-w-0"
          onClick={() => onOpen(apt)}
          data-testid={`history-appt-open-${apt.id}`}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{statusCfg.icon}</span>
            <p className="text-sm font-semibold text-gray-900 truncate">
              {apt.service_name || 'Training'}
            </p>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            🕐 {formatDateTime(apt.start_time)}
            {apt.end_time && ` - ${formatTime(apt.end_time)}`}
          </p>
        </button>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-1">
            <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`history-appt-delete-${apt.id}`}
              title="Termin stornieren"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          {apt.actual_price > 0 && (
            <p className="text-sm font-bold text-gray-900">
              CHF {formatCHF(apt.actual_price)}
            </p>
          )}
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">
            💳 {payment ? (payment.payment_method === 'bar' ? 'Bar' : payment.payment_method === 'karte_vor_ort' ? 'Karte (vor Ort)' : payment.payment_method === 'payrexx' ? 'Twint/Karte' : payment.payment_method) : 'Keine Zahlung'}
          </span>
          {payment && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-6"
              onClick={(e) => { e.stopPropagation(); onPaymentDetails(); }}
            >
              Details
            </Button>
          )}
        </div>
        <span className={payment && payment.status === 'completed' ? 'text-green-600 font-medium' : 'text-gray-400'}>
          {payment ? (payment.status === 'completed' ? '✓ Bezahlt' : 'Offen') : '—'}
        </span>
      </div>

      {apt.credit_used > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-purple-700">
          💰 CHF {formatCHF(apt.credit_used)} mit Guthaben bezahlt
        </div>
      )}
    </div>
  );
}

export default React.memo(ComprehensiveTransactions);
